import type { ClinicResponse } from '@/api/hospital';
import type { SubscriptionType } from '@/api/subscriptions';

/**
 * Which logging-period cadences a clinic offers, read off its
 * `clinic_subscription_types` flag (a comma-separated string).
 *
 * Shared by the Add Patient dialog and the logging-period form so both
 * answer the question the same way. The two differ only in whether "no
 * initial logging period" is a choice: it is when creating a patient, and
 * isn't when creating a period.
 */

/** Cadence plus the Add Patient dialog's "don't create one" option. */
export type Cadence = SubscriptionType | 'none';

/**
 * Display order, mirroring v1's `trialTypeOrder`
 * (clinician/src/components/addUser.tsx:498). `single` is the common case
 * and leads; `custom` and `none` sit at the end. `none` must not lead —
 * the dialogs snap to the first allowed option, so a leading `none` would
 * silently default every clinician to creating no logging period.
 */
export const CADENCE_ORDER: Cadence[] = [
  'single',
  'weekly',
  'bi-weekly',
  '4-weeks',
  'monthly',
  'custom',
  'none',
];

const allowedFromFlag = (clinic: ClinicResponse | undefined): Set<string> | null => {
  const raw = clinic?.flags?.clinic_subscription_types;
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  return new Set(raw.split(',').map((s) => s.trim()));
};

/**
 * Cadences the Add Patient dialog offers for the patient's first logging
 * period. `none` is always available; every real cadence — `custom`
 * included — has to be named by the clinic's flag.
 *
 * `custom` used to be appended unconditionally as a "freeform escape
 * hatch". That offered every clinic a cadence `POST /patients/from_clinician`
 * cannot create: the payload carries `subscription_name: 'custom'`, which
 * has no entry in the server's `SUBSCRIPTION_LENGTHS` (FOOD-1070). Which
 * cadences a clinic runs is a backend decision.
 */
export const addPatientCadences = (clinic: ClinicResponse | undefined): Cadence[] => {
  const allowed = allowedFromFlag(clinic);
  // v1's default when the flag is missing is `['single']`; `none` rides along.
  if (!allowed) return ['single', 'none'];
  const ordered = CADENCE_ORDER.filter((c) => c === 'none' || allowed.has(c));
  // `ordered` always contains 'none', so anything shorter than two entries
  // means the flag named nothing we recognise.
  return ordered.length <= 1 ? ['single', 'none'] : ordered;
};

/**
 * Cadences the logging-period form offers. Same rules, minus `none` —
 * the form exists to create a period.
 */
export const subscriptionCadences = (clinic: ClinicResponse | undefined): SubscriptionType[] => {
  const allowed = allowedFromFlag(clinic);
  if (!allowed) return ['single'];
  const ordered = CADENCE_ORDER.filter(
    (c): c is SubscriptionType => c !== 'none' && allowed.has(c),
  );
  return ordered.length === 0 ? ['single'] : ordered;
};
